// client/src/cognitive-flows/components/canvas/canvasUi.js
//
// "Ledger" identity tokens for the Analysis canvas surface. Paper-white
// workspace, ink-navy text, hairline borders, one interactive blue, and a
// per-block-type hue system (the surface's signature: color encodes block
// type on cards, palette pills and the drawer). Interaction states live in
// canvas.css (.cvx-*); these objects carry the static styling.

import { resolveBlockCharts } from './chartResolve';

export const ui = {
    paper: '#fcfcfb',
    surface: '#ffffff',
    ink: '#16233b',
    ink2: '#5c6b84',
    hairline: '#e3e6ec',
    border: '1px solid #e3e6ec',
    borderColor: '#e3e6ec',
    signal: '#2f5fe0',
    danger: '#c92f34',
    radius: '8px',
    radiusSm: '7px',
    shadowLift: '0 2px 8px rgba(22,35,59,0.07)',
    shadowOverlay: '0 12px 32px rgba(22,35,59,0.16)',

    // Aliases kept for call sites written before the Ledger pass.
    pageBg: '#fcfcfb',
    textPrimary: '#16233b',
    textSecondary: '#5c6b84',
    textMuted: '#8892a4',
    accent: '#2f5fe0',
    primary: '#16233b',
    shadow: '0 1px 2px rgba(22,35,59,0.04)',
    shadowHover: '0 2px 8px rgba(22,35,59,0.07)',
};

// The signature: each block type owns a hue (card spine, eyebrow, palette
// pill, drawer chip) and a soft tint for chips/backgrounds.
// Hues are dark enough that the card eyebrow (small text) clears WCAG AA on
// white — don't lighten these without re-checking contrast.
export const TYPE_HUES = {
    agent: '#6f42e8',
    chart: '#2856c9',
    table: '#0b6e63',
    scorecard: '#8a5a10',
    text: '#66738c',
    image: '#b23a6b',
    manualtable: '#0b6e63',   // shares the data-table hue: same thing, typed by hand
    pivot: '#1c5aa8',         // its own hue: a pivot reads as analysis, not as a table dump
};

/**
 * Blocks that hold their own content in `config` and never run — no data
 * source, no result, no Run button, and skipped by Refresh all.
 *
 * One set rather than a `!== 'text' && !== 'image'` test repeated at each
 * call site: those had already drifted apart across the card, the add-block
 * handler and refreshAll, so every new config-only type meant hunting for all
 * of them and quietly breaking whichever one was missed.
 */
export const CONFIG_ONLY_BLOCKS = new Set(['text', 'image', 'manualtable']);
export const isRunnableBlock = (type) => !CONFIG_ONLY_BLOCKS.has(type);

/**
 * Which half of an agent block's viz is currently on show.
 *
 * An agent result may carry BOTH a chart and a table (shapeAgentBlockObject
 * keeps both when the answer had both). `viz.type` is the DEFAULT view — its
 * meaning is unchanged from before the toggle existed, which is what keeps
 * every previously-stored result rendering as it did — and `config.vizView` is
 * the reader's override, meaningful only when both halves are present.
 *
 * Lives here, not in CanvasBlockCard, because the card and the RESULT EDITOR
 * must agree: the editor edits whichever half the card is showing, and if the
 * two derived it separately they would drift and the editor would silently
 * rewrite the half nobody was looking at.
 *
 * `entries` is `resolveBlockCharts(block)` — a chart derived by the reader's
 * settings counts as a chart.
 */
export function agentVizView(block, viz) {
    // Resolved, not raw: the reader's chart settings can restyle a chart or
    // derive one from the table, and a derived chart is a chart the toggle
    // must know about. `entries` carries the issues each chart resolved with.
    const entries = block ? resolveBlockCharts(block) : [];
    const charts = entries.map((e) => e.chart).filter(Boolean);
    const hasChart = charts.length > 0;
    const hasTable = Array.isArray(viz?.table) && viz.table.length > 0;
    if (!hasChart && !hasTable) return { hasChart, hasTable, view: null, charts, entries };
    if (!hasChart) return { hasChart, hasTable, view: 'table', charts, entries };
    if (!hasTable) return { hasChart, hasTable, view: 'chart', charts, entries };
    const chosen = block?.config?.vizView;
    if (chosen === 'chart' || chosen === 'table') return { hasChart, hasTable, view: chosen, charts, entries };
    return { hasChart, hasTable, view: viz.type === 'table' ? 'table' : 'chart', charts, entries };
}

export const TYPE_TINTS = {
    agent: '#f1ebff',
    chart: '#e9effc',
    table: '#e2f5f2',
    scorecard: '#faf0dd',
    text: '#eef0f4',
    image: '#fbebf1',
    manualtable: '#e2f5f2',
    pivot: '#e6eefa',
};

// Tracked-uppercase micro-label — every structural label on the surface.
export const microLabel = {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: ui.ink2,
};

export const btnPrimary = {
    display: 'inline-flex', alignItems: 'center', gap: '7px',
    padding: '9px 16px', borderRadius: ui.radiusSm, border: 'none',
    background: ui.signal, color: '#fff', fontSize: '13px', fontWeight: 650,
    cursor: 'pointer', fontFamily: 'inherit',
};

// border/background live in canvas.css (.cvx-ghost) so :hover can change
// them — inline values would beat the stylesheet. Pair with className.
export const btnGhost = {
    display: 'inline-flex', alignItems: 'center', gap: '7px',
    padding: '8px 14px', borderRadius: ui.radiusSm,
    color: ui.ink, fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
};

// background/color live in canvas.css (.cvx-iconbtn) for the same reason.
export const btnIcon = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '30px', height: '30px', borderRadius: ui.radiusSm,
    cursor: 'pointer', fontFamily: 'inherit', padding: 0,
};
